import React, { useEffect, useState, Suspense } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Box,
  Paper,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useDispatch, useSelector } from "react-redux";
import { fetchProjectsByUserId } from "./redux/projectThunk";
import userStore from "../../../zustand/userStore";
import { styled } from "@mui/system";

// Styled Accordion Summary with animated arrows and gradient background
const StyledAccordionSummary = styled(AccordionSummary)(({ theme, expanded }) => ({
  background: "linear-gradient(to right, #7B2FF7, #F40076)",
  color: "#fff",
  borderBottom: "1px solid #ccc",
  borderRadius: "8px",
  "& .MuiAccordionSummary-expandIconWrapper":  {
    color: "#fff",
    transition: "transform 0.3s ease",
    transform: expanded === "true" ? "rotate(180deg)" : "rotate(0deg)",
  },
  "&:hover": {
    // background: "linear-gradient(to right, #5e1ccf, #c6005c)",
  },
}));

const groupByEventTitle = (data) => {
  const grouped = {};
  data.forEach((item) => {
    const key = item.event_title || "Unknown Event";
    if (!grouped[key]) {
      grouped[key] = [];
    }
    grouped[key].push(item);
  });
  return grouped;
};

const ProjectAccordionList = () => {
  const dispatch = useDispatch();
  const userId = userStore.getState().user.id;
  const { data, loading, error } = useSelector((state) => state.project);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (userId) {
      dispatch(fetchProjectsByUserId(userId));
    }
  }, [dispatch, userId]);

  const groupedData = groupByEventTitle(data);
  const groupedArray = Object.entries(groupedData);

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Suspense fallback={<>Loading...</>}>
      <div className="main_container">
        <div
          className="right_col calender-right-col"
          role="main"
          style={{ overflow: "auto" }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: "1100px",
              margin: "0 auto",
              pb: 8,
              minHeight: "100vh",
            }}
          >
            <Typography
              variant="h4"
              align="center"
              gutterBottom
              sx={{
                fontWeight: "bold",
                // background: "linear-gradient(to right, #7B2FF7, #F40076)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Project Events
            </Typography>

            {loading && <p>Loading...</p>}
            {error && <p style={{ color: "red" }}>Error: {error}</p>}

            <Paper
              elevation={6}
              sx={{
                px: 3,
                py: 2,
                borderRadius: 3,
                // backgroundColor: "#f3e5f5",
                border: "2px solid #7B2FF7",
                // boxShadow: "0px 8px 24px rgba(123, 47, 247, 0.3)",
              }}
            >
              {groupedArray.map(([eventTitle, projects], idx) => (
                <Accordion
                  key={idx}
                  expanded={expanded === eventTitle}
                  onChange={handleAccordionChange(eventTitle)}
                  sx={{
                    mb: 2,
                    borderRadius: 2,
                    backgroundColor: "#f8e6f0",
                    "&:before": {
                      display: "none",
                    },
                  }}
                >
                  <StyledAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    expanded={(expanded === eventTitle).toString()}
                  >
                    <Typography fontWeight="bold" fontSize="1.1rem">
                      {eventTitle} ({projects[0].event_type})
                    </Typography>
                  </StyledAccordionSummary>
                  <AccordionDetails>
                    {(() => {
                      const parents = projects.filter((p) => !p.parent_name);
                      const children = projects.filter((p) => p.parent_name);

                      const parentMap = {};
                      parents.forEach((parent) => {
                        parentMap[parent.project_name] = {
                          ...parent,
                          children: [],
                          isVirtual: false,
                        };
                      });

                      children.forEach((child) => {
                        const parentKey = child.parent_name;
                        if (!parentMap[parentKey]) {
                          parentMap[parentKey] = {
                            project_name: parentKey,
                            isVirtual: true,
                            children: [],
                          };
                        }
                        parentMap[parentKey].children.push(child);
                      });

                      const finalGrouped = Object.values(parentMap);

                      return finalGrouped.map((group, i) => (
                        <Box key={`group-${i}`} sx={{ mb: 2 }}>
                          <Typography
                            variant="subtitle1"
                            sx={{
                              fontWeight: group.isVirtual ? "normal" : "bold",
                              color: "#6a1b9a",
                            }}
                          >
                            {i + 1}. {group.project_name}
                          </Typography>

                          {!group.isVirtual && (
                            <Typography variant="body2" color="text.secondary">
                              Status: {group.status === 1 ? "Active" : "Inactive"} | Created:{" "}
                              {new Date(group.created_at).toLocaleString()}
                            </Typography>
                          )}

                          {group.children.length > 0 && (
                            <Box sx={{ ml: 2, mt: 1 }}>
                              {group.children.map((child, j) => (
                                <Box key={child.project_id} sx={{ mb: 1 }}>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontStyle: "italic", color: "#4a148c" }}
                                  >
                                    → {j + 1}. {child.project_name}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ ml: 2 }}
                                  >
                                    Status: {child.status === 1 ? "Active" : "Inactive"} | Created:{" "}
                                    {new Date(child.created_at).toLocaleString()}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {i !== finalGrouped.length - 1 && (
                            <Divider sx={{ my: 1, borderColor: "#ce93d8" }} />
                          )}
                        </Box>
                      ));
                    })()}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Paper>
          </Box>
        </div>
      </div>
    </Suspense>
  );
};

export default ProjectAccordionList;
